import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebaseConfig"; // Ensure the path is correct
import "./styles/App.css";
// Load every page's CSS up front in the original order so shared popup/button
// styles (.modal, .modal-overlay...) stay the same no matter which page opened
// first. Page code itself is still lazy-loaded. See styles/pageStyles.js.
import "./styles/pageStyles";
import { lazyWithFallback } from "./utils/lazyWithFallback";
import { useLanguage } from "./i18n";
import { getViewAs, stopViewAs } from "./utils/viewAs";
import { useChecklistAccess } from "./utils/checklistAccess";
import { ResumeAfterReload } from "./utils/lazyWithFallback";

// Import your pages
import SignIn from "./pages/SignIn";
import Home from "./pages/Home";

// Pages load on first visit (smaller first download = faster app start).
const Scheduling = lazyWithFallback(() => import("./pages/Scheduling"));
const AddItem = lazyWithFallback(() => import("./pages/AddItem"));
const BillsCredits = lazyWithFallback(() => import("./pages/BillsCredits"));
const Calendar = lazyWithFallback(() => import("./pages/Calendar"));
const Contractors = lazyWithFallback(() => import("./pages/Contractors"));
const ContractorPay = lazyWithFallback(() => import("./pages/ContractorPay"));
const CustomerCodes = lazyWithFallback(() => import("./pages/CustomerCodes"));
const Maps = lazyWithFallback(() => import("./pages/Maps"));
const Notebook = lazyWithFallback(() => import("./pages/Notebook"));
const PropertiesClients = lazyWithFallback(() => import("./pages/PropertiesClients"));
const Management = lazyWithFallback(() => import("./pages/Management"));
const ProposalsJMC = lazyWithFallback(() => import("./pages/ProposalsJMC"));
const ProposalsDCS = lazyWithFallback(() => import("./pages/ProposalsDCS"));
const SendInvoices = lazyWithFallback(() => import("./pages/SendInvoices"));
const Settings = lazyWithFallback(() => import("./pages/Settings"));
const Staff = lazyWithFallback(() => import("./pages/Staff"));
const Vendors = lazyWithFallback(() => import("./pages/Vendors"));
const WorkOrders = lazyWithFallback(() => import("./pages/WorkOrders"));
const AdminPortal = lazyWithFallback(() => import("./pages/AdminPortal"));
const UserManagement = lazyWithFallback(() => import("./pages/UserManagement"));
const ClockTime = lazyWithFallback(() => import("./pages/ClockTime"));
const Supplies = lazyWithFallback(() => import("./pages/Supplies"));
const FireWatch = lazyWithFallback(() => import("./pages/FireWatch"));
const NeedsScheduling = lazyWithFallback(() => import("./pages/NeedsScheduling"));
const SentProposals = lazyWithFallback(() => import("./pages/SentProposals"));
const LicenseCertificates = lazyWithFallback(() => import("./pages/LicenseCertificates"));
const Reconcile = lazyWithFallback(() => import("./pages/Reconcile"));
const KondeElectric = lazyWithFallback(() => import("./pages/KondeElectric"));
const Projetcs = lazyWithFallback(() => import("./pages/Projects"));
const Vehicles = lazyWithFallback(() => import("./pages/Vehicles"));
const Spreadsheet = lazyWithFallback(() => import("./pages/Spreadsheet"));
const SpreadsheetAdmin = lazyWithFallback(() => import("./pages/SpreadsheetAdmin"));
const Eula = lazyWithFallback(() => import("./pages/Eula"));
const PrivacyPolicy = lazyWithFallback(() => import("./pages/PrivacyPolicy"));
const DataBase = lazyWithFallback(() => import("./pages/DataBase"));
const JobChecklist = lazyWithFallback(() => import("./pages/JobChecklist"));

/* The Checklist route follows the Admin's Staff Settings choice for techs. */
function ChecklistRoute({ userRole, children }) {
  const { canSee, loading } = useChecklistAccess(userRole);
  if (!userRole || loading) return null;
  return canSee ? children : <Navigate to="/" replace />;
}
const TimeOff = lazyWithFallback(() => import("./pages/TimeOff"));
const TimeOffAdmin = lazyWithFallback(() => import("./pages/TimeOffAdmin"));
const AdminAccess = lazyWithFallback(() => import("./pages/AdminAccess"));
const QuoteBuilder = lazyWithFallback(() => import("./pages/QuoteBuilder"));
const PaymentsReceived = lazyWithFallback(() => import("./pages/PaymentsReceived"));
const RemindersPage = lazyWithFallback(() => import("./pages/RemindersPage"));
const SmartBoardList = lazyWithFallback(() => import("./pages/SmartBoardList"));
const CompletionBoard = lazyWithFallback(() => import("./pages/CompletionBoard"));
const ServiceTypesAdmin = lazyWithFallback(() => import("./pages/ServiceTypesAdmin"));
const DataBackups = lazyWithFallback(() => import("./pages/DataBackups"));
const WOTotalsRepair = lazyWithFallback(() => import("./pages/WOTotalsRepair"));
const BulkFixImport = lazyWithFallback(() => import("./pages/BulkFixImport"));
const ContractBilling = lazyWithFallback(() => import("./pages/ContractBilling"));
const JobProfit = lazyWithFallback(() => import("./pages/JobProfit"));
const FinancialsDashboard = lazyWithFallback(() => import("./pages/FinancialsDashboard"));
const OverheadSettings = lazyWithFallback(() => import("./pages/OverheadSettings"));
const JobFinancials = lazyWithFallback(() => import("./pages/JobFinancials"));
const DailyLabor = lazyWithFallback(() => import("./pages/DailyLabor"));
const DailySpending = lazyWithFallback(() => import("./pages/DailySpending"));
const CompletedProposals = lazyWithFallback(() => import("./pages/CompletedProposals"));
const ApprovedProposals = lazyWithFallback(() => import("./pages/ApprovedProposals"));
const DCSCompletedProposals = lazyWithFallback(() => import("./pages/DCSCompletedProposals"));
const DCSApprovedProposals = lazyWithFallback(() => import("./pages/DCSApprovedProposals"));
const DCSSentProposals = lazyWithFallback(() => import("./pages/DCSSentProposals"));
const Deposits = lazyWithFallback(() => import("./pages/Deposits"));
const Expenses = lazyWithFallback(() => import("./pages/Expenses"));
const ProfitSharing = lazyWithFallback(() => import("./pages/ProfitSharing"));
const SupervisorDashboard = lazyWithFallback(() => import("./pages/SupervisorDashboard"));
const SubMeter = lazyWithFallback(() => import("./pages/SubMeter"));
const PiedmontSubMeter = lazyWithFallback(() => import("./pages/PiedmontSubMeter"));
const JobHistory = lazyWithFallback(() => import("./pages/JobHistory"));
const InvoicePPD = lazyWithFallback(() => import("./pages/InvoicePPD"));
const InvoiceBuilderPPD = lazyWithFallback(() => import("./pages/InvoiceBuilderPPD"));
const InvoiceDropdownsAdmin = lazyWithFallback(() => import("./pages/InvoiceDropdownsAdmin"));
const ProposalsJMCDropdownsAdmin = lazyWithFallback(() => import("./pages/ProposalsJMCDropdownsAdmin"));
const ProposalsDCSDropdownsAdmin = lazyWithFallback(() => import("./pages/ProposalsDCSDropdownsAdmin"));
const SupervisorPortal = lazyWithFallback(() => import("./pages/SupervisorPortal"));
const PropertyProjects = lazyWithFallback(() => import("./pages/PropertyProjects"));
const ProposalsMHI = lazyWithFallback(() => import("./pages/ProposalsMHI"));
const ProposalsOGR = lazyWithFallback(() => import("./pages/ProposalsOGR"));
const JobFinancialHub = lazyWithFallback(() => import("./pages/JobFinancialHub"));
const TrainingVideos = lazyWithFallback(() => import("./pages/TrainingVideos"));
const AccountingExports = lazyWithFallback(() => import("./pages/AccountingExports"));
const JobEstimateSpreadsheet = lazyWithFallback(() => import("./pages/JobEstimateSpreadsheet"));
const NotificationRecipients = lazyWithFallback(() => import("./pages/NotificationRecipients"));
const NotesTasks = lazyWithFallback(() => import("./pages/NotesTasks"));
const RoughEstimateBuilder = lazyWithFallback(() => import("./pages/RoughEstimateBuilder"));
const EstimateBuilderAdmin = lazyWithFallback(() => import("./pages/EstimateBuilderAdmin"));
const CashPlanDashboard = lazyWithFallback(() => import("./pages/CashPlanDashboard"));
const PlanEstimator = lazyWithFallback(() => import("./pages/PlanEstimator"));
const DailyChecklist = lazyWithFallback(() => import("./pages/DailyChecklist"));
const Takeoff = lazyWithFallback(() => import("./pages/Takeoff"));
const CallLog = lazyWithFallback(() => import("./pages/CallLog"));
const JMCReports = lazyWithFallback(() => import("./pages/JMCReports"));
const JMCReportsDropdownsAdmin = lazyWithFallback(() => import("./pages/JMCReportsDropdownsAdmin"));
const ApprovedInvoices = lazyWithFallback(() => import("./pages/ApprovedInvoices"));
const UnassignedClockIns = lazyWithFallback(() => import("./pages/UnassignedClockIns"));
const DCSNotApprovedProposals = lazyWithFallback(() => import("./pages/DCSNotApprovedProposals"));
const MHINotApprovedProposals = lazyWithFallback(() => import("./pages/MHINotApprovedProposals"));
const OGRNotApprovedProposals = lazyWithFallback(() => import("./pages/OGRNotApprovedProposals"));
// import ClockIn from "./pages/ClockIn";


function App() {
  const { t } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Add a loading state
  // Set when the signed-in user's profile could not be read (slow/no signal).
  // Shows a "Try again" screen instead of silently dropping to Sign In.
  const [roleLoadError, setRoleLoadError] = useState("");
  const [roleReloadKey, setRoleReloadKey] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userId = user.uid;
        setRoleLoadError("");

        // Up to 3 tries - a weak phone signal often fails the first read.
        let lastError = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            // Fetch user role from Firestore
            const userDocRef = doc(db, "users", userId);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              // "View as" is Admin-only: anyone else gets it switched off.
              if (userDoc.data().role !== "Admin" && getViewAs()) stopViewAs({ reload: false });
              setUserRole(userDoc.data().role);
              setIsLoggedIn(true);
            } else {
              console.error("User role not found.");
              setIsLoggedIn(false);
            }
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            console.error(`Error fetching user role (try ${attempt + 1}):`, error);
            await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
          }
        }
        if (lastError) {
          setRoleLoadError(lastError?.message || "Could not load your account.");
        }
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
      }
      setIsLoading(false); // Stop loading once Firebase resolves the user
    });

    return () => unsubscribe();
  }, [roleReloadKey]);

  const handleSignOut = async () => {
    stopViewAs({ reload: false });
    try {
      await signOut(auth);
      setIsLoggedIn(false);
      setUserRole(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Define role-based menu items
  const menuItems = {
    Tech: [
   //   { label: "Home", path: "/" },
   
    ],
    Admin: [
     // { label: "Clock In", path: "/ClockIn" },



    //  { label: "Spreadsheet", path: "/Spreadsheet" },
      // { label: "Privacy Policy", path: "/PrivacyPolicy" },



    ],
    Manager: [
     // { label: "Spreadsheet", path: "/Spreadsheet" },


    ],
    KondeElectric: [
      //{ label: "Customer Codes", path: "/CustomerCodes" },

    ],

    Assistant: [


      //{ label: "Spreadsheet", path: "/Spreadsheet" },

    ],
  };

  // Sort menu items for each role alphabetically by label
  Object.keys(menuItems).forEach(role => {
    menuItems[role] = menuItems[role].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  });

  if (isLoading) {
    return <div style={{ padding: 32, textAlign: "center", fontWeight: 600 }}>{t("Loading...")}</div>;
  }

  if (roleLoadError) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <p style={{ fontWeight: 700 }}>{t("Could not load your account. Check your connection.")}</p>
        <button
          onClick={() => {
            setIsLoading(true);
            setRoleLoadError("");
            setRoleReloadKey((k) => k + 1);
          }}
          style={{ padding: "10px 16px", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}
        >
          {t("Try again")}
        </button>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <Router>
        <SignIn setUserRole={setUserRole} />
      </Router>
    );
  }

  return (
    <Router>
      <ResumeAfterReload />
      <header className="header">
        <div className="logo">
          <img src="/logo.png" alt="Servicing HQ" />
        </div>
        <div className="app-title">{t("Welcome to Your Servicing HQ Portal")}</div>
        <button className="signout-button" onClick={handleSignOut}>{t("Sign Out")}</button>
      </header>

      {userRole === "Admin" && getViewAs() && (
        <div
          role="status"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "8px 12px",
            background: "#ea580c",
            color: "#fff",
            fontWeight: 800,
            position: "sticky",
            top: 0,
            zIndex: 9000,
          }}
        >
          <span>
            👁 {t("Viewing as {name} ({role}) · read-only", { name: getViewAs().name, role: getViewAs().role })}
          </span>
          <button
            type="button"
            className="tjt-btn"
            onClick={() => stopViewAs()}
            style={{ width: "auto", minHeight: 0, padding: "6px 14px", background: "#fff", color: "#9a3412", borderRadius: 8 }}
          >
            {t("Exit")}
          </button>
        </div>
      )}

      <nav className="menu">
        {userRole &&
          menuItems[userRole]?.map((item) => (
            <Link key={item.path} to={item.path}>
              {item.label}
            </Link>
          ))}
      </nav>

      <div className="content">
        <Routes>
          <Route
            path="/"
            element={userRole === "KondeElectric" ? <KondeElectric /> : <Home />}
          />
          {/* Office staff always; techs only when the Admin allows it in
              Staff Settings (everyone, or picked people). Anyone else
              opening the link goes Home. */}
          <Route
            path="/JobChecklist/:jobId"
            element={<ChecklistRoute userRole={userRole}><JobChecklist /></ChecklistRoute>}
          />
          {userRole === "Tech" && (
            <>
              <Route path="/AddItem" element={<AddItem />} />
              <Route path="/Calendar" element={<Calendar />} />
          {/*    <Route path="/ClockIn" element={<ClockIn />} /> */}
              <Route path="/Maps" element={<Maps />} />
              <Route path="/CustomerCodes" element={<CustomerCodes />} />
              <Route path="/Supplies" element={<Supplies />} />
              <Route path="/FireWatch" element={<FireWatch />} />
              <Route path="/Eula" element={<Eula />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route path="/TimeOff" element={<TimeOff />} />
            </>
          )}
          {userRole === "Admin" && (
            <>
              <Route path="/CompletionBoard" element={<CompletionBoard />} />
              <Route path="/ServiceTypesAdmin" element={<ServiceTypesAdmin />} />
              <Route path="/DataBackups" element={<DataBackups />} />
              <Route path="/WOTotalsRepair" element={<WOTotalsRepair />} />
              <Route path="/BulkFixImport" element={<BulkFixImport />} />
              <Route path="/ContractBilling" element={<ContractBilling />} />
              <Route path="/JobProfit" element={<JobProfit />} />
              <Route path="/AdminPortal" element={<AdminPortal />} />
              <Route path="/Scheduling" element={<Scheduling />} />
              <Route path="/AddItem" element={<AddItem />} />
              <Route path="/Calendar" element={<Calendar />} />
           {/*     <Route path="/ClockIn" element={<ClockIn />} /> */}
              <Route path="/ClockTime" element={<ClockTime />} />
              <Route path="/WorkOrders" element={<WorkOrders />} />
              <Route path="/Maps" element={<Maps />} />
              <Route path="/Contractors" element={<Contractors />} />
              <Route path="/ContractorPay" element={<ContractorPay />} />
              <Route path="/CustomerCodes" element={<CustomerCodes />} />
              <Route path="/Notebook" element={<Notebook />} />
              <Route path="/PropertiesClients" element={<PropertiesClients />} />
              <Route path="/ProposalsJMC" element={<ProposalsJMC />} />
              <Route path="/ProposalsDCS" element={<ProposalsDCS />} />
              <Route path="/ProposalsOGR" element={<ProposalsOGR />} />
              <Route path="/ProposalsMHI" element={<ProposalsMHI />} />
              <Route path="/SendInvoices" element={<SendInvoices />} />
              <Route path="/Staff" element={<Staff />} />
              <Route path="/Vendors" element={<Vendors />} />
              <Route path="/BillsCredits" element={<BillsCredits />} />
              <Route path="/UserManagement" element={<UserManagement />} />
              <Route path="/Supplies" element={<Supplies />} />
              <Route path="/Management" element={<Management />} />
              <Route path="/Settings" element={<Settings />} />
              <Route path="/FireWatch" element={<FireWatch />} />
              <Route path="/NeedsScheduling" element={<NeedsScheduling />} />
              <Route path="/SentProposals" element={<SentProposals />} />
              <Route path="/LicenseCertificates" element={<LicenseCertificates />} />
              <Route path="/Reconcile" element={<Reconcile />} />
              <Route path="/KondeElectric" element={<KondeElectric />} />
              <Route path="/Projects" element={<Projetcs />} />
              <Route path="/Vehicles" element={<Vehicles />} />
              <Route path="/Spreadsheet" element={<Spreadsheet />} />
              <Route path="/SpreadsheetAdmin" element={<SpreadsheetAdmin />} />
              <Route path="/Eula" element={<Eula />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route path="/DataBase" element={<DataBase />} />
              <Route path="/TimeOff" element={<TimeOff />} />
              <Route path="/TimeOffAdmin" element={<TimeOffAdmin />} />
              <Route path="/AdminAccess" element={<AdminAccess />} />
              <Route path="/QuoteBuilder" element={<QuoteBuilder />} />
              <Route path="/PaymentsReceived" element={<PaymentsReceived />} />
              <Route path="/RemindersPage" element={<RemindersPage />} />
              <Route path="/SmartBoardList" element={<SmartBoardList />} />
              <Route path="/Financials" element={<FinancialsDashboard />} />
<Route path="/OverheadSettings" element={<OverheadSettings />} />
<Route path="/JobFinancials/:id" element={<JobFinancials />} />
<Route path="/DailyLabor" element={<DailyLabor />} />
<Route path="/DailySpending" element={<DailySpending />} />
<Route path="/ApprovedProposals" element={<ApprovedProposals />} />
<Route path="/CompletedProposals" element={<CompletedProposals />} />
<Route path="/DCSApprovedProposals" element={<DCSApprovedProposals />} />
<Route path="/DCSCompletedProposals" element={<DCSCompletedProposals />} />
<Route path="/DCSSentProposals" element={<DCSSentProposals />} />
<Route path="/Deposits" element={<Deposits />} />
<Route path="/Expenses" element={<Expenses />} />
<Route path="/ProfitSharing" element={<ProfitSharing />} />
<Route path="/SupervisorDashboard" element={<SupervisorPortal />} />
<Route path="/SubMeter" element={<SubMeter />} />
<Route path="/PiedmontSubMeter" element={<PiedmontSubMeter />} />
<Route path="/JobHistory" element={<JobHistory />} />
<Route path="/InvoicePPD" element={<InvoicePPD />} />
<Route
  path="/invoice"
  element={<InvoiceBuilderPPD />}
/>
<Route path="/JMCReports" element={<JMCReports />} />
<Route path="/JMCReportsDropdownsAdmin" element={<JMCReportsDropdownsAdmin />} />
<Route path="/InvoiceDropdownsAdmin" element={<InvoiceDropdownsAdmin />} />
<Route path="/CashPlanDashboard" element={<CashPlanDashboard />} />
<Route path="/ProposalsJMCDropdownsAdmin" element={<ProposalsJMCDropdownsAdmin />} />
<Route path="/ProposalsDCSDropdownsAdmin" element={<ProposalsDCSDropdownsAdmin />} />
<Route path="/PropertyProjects" element={<PropertyProjects />} />
<Route path="/job-financials" element={<JobFinancialHub />} />
<Route path="/TrainingVideos" element={<TrainingVideos />} />
<Route path="/accounting-exports" element={<AccountingExports />} />
<Route path="/job-estimate-spreadsheet" element={<JobEstimateSpreadsheet />} />
<Route path="/ApprovedInvoices" element={<ApprovedInvoices />} />
<Route path="/UnassignedClockIns" element={<UnassignedClockIns />} />
<Route path="/notification-recipients" element={<NotificationRecipients />} />
<Route path="/NotesTasks" element={<NotesTasks />} />
<Route path="/PlanEstimator" element={<PlanEstimator />} />
<Route path="/DailyChecklist" element={<DailyChecklist />} />
<Route path="/Takeoff" element={<Takeoff />} />
<Route
  path="/rough-estimate"
  element={<RoughEstimateBuilder />}
/>

<Route
  path="/admin/estimate-builder"
  element={<EstimateBuilderAdmin />}
/>
<Route path="/call-log" element={<CallLog />} />
<Route path="/DCSNotApprovedProposals" element={<DCSNotApprovedProposals />} />
<Route path="/MHINotApprovedProposals" element={<MHINotApprovedProposals />} />
<Route path="/OGRNotApprovedProposals" element={<OGRNotApprovedProposals />} />
            </>
          )}
          {userRole === "Manager" && (
            <>
              <Route path="/CompletionBoard" element={<CompletionBoard />} />
              <Route path="/ServiceTypesAdmin" element={<ServiceTypesAdmin />} />
              <Route path="/DataBackups" element={<DataBackups />} />
              <Route path="/WOTotalsRepair" element={<WOTotalsRepair />} />
              <Route path="/BulkFixImport" element={<BulkFixImport />} />
              <Route path="/ContractBilling" element={<ContractBilling />} />
              <Route path="/JobProfit" element={<JobProfit />} />
              <Route path="/AdminPortal" element={<AdminPortal />} />
              <Route path="/Scheduling" element={<Scheduling />} />
              <Route path="/AddItem" element={<AddItem />} />
              <Route path="/Calendar" element={<Calendar />} />
          {/*      <Route path="/ClockIn" element={<ClockIn />} /> */}
              <Route path="/WorkOrders" element={<WorkOrders />} />
              <Route path="/Maps" element={<Maps />} />
              <Route path="/Contractors" element={<Contractors />} />
              <Route path="/CustomerCodes" element={<CustomerCodes />} />
              <Route path="/Notebook" element={<Notebook />} />
              <Route path="/PropertiesClients" element={<PropertiesClients />} />
              <Route path="/ProposalsJMC" element={<ProposalsJMC />} />
              <Route path="/ProposalsDCS" element={<ProposalsDCS />} />
              <Route path="/SendInvoices" element={<SendInvoices />} />
              <Route path="/Vendors" element={<Vendors />} />
              <Route path="/Supplies" element={<Supplies />} />
              <Route path="/Management" element={<Management />} />
              <Route path="/ContractorPay" element={<ContractorPay />} />
              <Route path="/FireWatch" element={<FireWatch />} />
              <Route path="/NeedsScheduling" element={<NeedsScheduling />} />
              <Route path="/SentProposals" element={<SentProposals />} />
              <Route path="/Projects" element={<Projetcs />} />
              <Route path="/Vehicles" element={<Vehicles />} />
              <Route path="/Spreadsheet" element={<Spreadsheet />} />
              <Route path="/Eula" element={<Eula />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route path="/TimeOff" element={<TimeOff />} />
              <Route path="/RemindersPage" element={<RemindersPage />} />
              <Route path="/ApprovedProposals" element={<ApprovedProposals />} />
<Route path="/CompletedProposals" element={<CompletedProposals />} />
<Route path="/DCSApprovedProposals" element={<DCSApprovedProposals />} />
<Route path="/DCSCompletedProposals" element={<DCSCompletedProposals />} />
<Route path="/DCSSentProposals" element={<DCSSentProposals />} />
<Route path="/SubMeter" element={<SubMeter />} />
<Route path="/PiedmontSubMeter" element={<PiedmontSubMeter />} />
<Route path="/JobHistory" element={<JobHistory />} />
<Route path="/TrainingVideos" element={<TrainingVideos />} />
<Route path="/accounting-exports" element={<AccountingExports />} />
<Route
  path="/rough-estimate"
  element={<RoughEstimateBuilder />}
/>
<Route path="/JMCReports" element={<JMCReports />} />
<Route path="/JMCReportsDropdownsAdmin" element={<JMCReportsDropdownsAdmin />} />

<Route
  path="/admin/estimate-builder"
  element={<EstimateBuilderAdmin />}
/>
            </>
          )}
          {userRole === "KondeElectric" && (
            <>
              <Route path="/KondeElectric" element={<KondeElectric />} />
              <Route path="/Maps" element={<Maps />} />
              <Route path="/CustomerCodes" element={<CustomerCodes />} />
              <Route path="/Eula" element={<Eula />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
            </>
          )}
          {userRole === "Assistant" && (
            <>
              <Route path="/CompletionBoard" element={<CompletionBoard />} />
              <Route path="/ServiceTypesAdmin" element={<ServiceTypesAdmin />} />
              <Route path="/AdminPortal" element={<AdminPortal />} />
              <Route path="/Scheduling" element={<Scheduling />} />
              <Route path="/AddItem" element={<AddItem />} />
              <Route path="/Calendar" element={<Calendar />} />
           {/*     <Route path="/ClockIn" element={<ClockIn />} /> */}
              <Route path="/ClockTime" element={<ClockTime />} />
              <Route path="/WorkOrders" element={<WorkOrders />} />
              <Route path="/Maps" element={<Maps />} />
              <Route path="/Contractors" element={<Contractors />} />
              <Route path="/CustomerCodes" element={<CustomerCodes />} />
              <Route path="/Notebook" element={<Notebook />} />
              <Route path="/PropertiesClients" element={<PropertiesClients />} />
              <Route path="/ProposalsJMC" element={<ProposalsJMC />} />
              <Route path="/ProposalsDCS" element={<ProposalsDCS />} />
              <Route path="/SendInvoices" element={<SendInvoices />} />
              <Route path="/Vendors" element={<Vendors />} />
              <Route path="/Supplies" element={<Supplies />} />
              <Route path="/Management" element={<Management />} />
              <Route path="/FireWatch" element={<FireWatch />} />
              <Route path="/NeedsScheduling" element={<NeedsScheduling />} />
              <Route path="/SentProposals" element={<SentProposals />} />
              <Route path="/Projects" element={<Projetcs />} />
              <Route path="/Vehicles" element={<Vehicles />} />
              <Route path="/Spreadsheet" element={<Spreadsheet />} />
              <Route path="/Eula" element={<Eula />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route path="/TimeOff" element={<TimeOff />} />
              <Route path="/RemindersPage" element={<RemindersPage />} />
              <Route path="/ApprovedProposals" element={<ApprovedProposals />} />
<Route path="/CompletedProposals" element={<CompletedProposals />} />
<Route path="/DCSApprovedProposals" element={<DCSApprovedProposals />} />
<Route path="/DCSCompletedProposals" element={<DCSCompletedProposals />} />
<Route path="/DCSSentProposals" element={<DCSSentProposals />} />
<Route path="/SubMeter" element={<SubMeter />} />
<Route path="/PiedmontSubMeter" element={<PiedmontSubMeter />} />
<Route path="/JobHistory" element={<JobHistory />} />
<Route path="/TrainingVideos" element={<TrainingVideos />} />
<Route path="/accounting-exports" element={<AccountingExports />} />
<Route
  path="/rough-estimate"
  element={<RoughEstimateBuilder />}
/>
<Route path="/JMCReports" element={<JMCReports />} />
<Route path="/JMCReportsDropdownsAdmin" element={<JMCReportsDropdownsAdmin />} />
<Route
  path="/admin/estimate-builder"
  element={<EstimateBuilderAdmin />}
/>
            </>
            )}
             {userRole === "PiedmontBoard" && (
            <>
              <Route path="/PiedmontSubMeter" element={<PiedmontSubMeter />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
            </>
          )}
        </Routes>
      </div>
    </Router>
  );
}

export default App;